// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import { Box, SegmentedControl } from '@mantine/core';

import type { RenderMode } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewRenderer';
import classes from 'modules/Shop/EvolveFabrication/BendCreator/Shell/BendCreatorShell.module.css';
import { ShellExitButton } from 'modules/Shop/EvolveFabrication/BendCreator/Shell/ShellExitButton';

export type ShellViewMode = RenderMode | 'layout';

const viewOptions: { label: string; value: ShellViewMode }[] = [
  { label: 'Iso', value: '3d' },
  { label: 'Front', value: '2dFront' },
  { label: 'Top', value: '2dTop' },
  { label: 'Layout', value: 'layout' },
];

type Props = {
  viewMode: ShellViewMode;
  onViewModeChange: (viewMode: ShellViewMode) => void;
  /** Called when the user presses anything in the top bar, by pointer or key. */
  onInteract: () => void;
  /** When false, the Layout view is not offered. */
  layoutAvailable: boolean;
  /** When omitted, no Exit button is shown. */
  onClose?: () => void;
  bendsToDiscard: number;
};

export const ShellTopBar = ({
  viewMode,
  onViewModeChange,
  onInteract,
  layoutAvailable,
  onClose,
  bendsToDiscard,
}: Props) => {
  const availableViewOptions = useMemo(
    () => (layoutAvailable ? viewOptions : viewOptions.filter(({ value }) => value !== 'layout')),
    [layoutAvailable],
  );

  return (
    <Box className={classes.topBar} onPointerDown={onInteract} onKeyDown={onInteract}>
      <SegmentedControl
        aria-label="View"
        value={viewMode}
        onChange={onViewModeChange}
        data={availableViewOptions}
        classNames={{
          root: classes.viewSegmentRoot,
          control: classes.viewSegmentControl,
          indicator: classes.viewSegmentIndicator,
          label: classes.viewSegmentLabel,
        }}
      />
      {onClose && <ShellExitButton onExit={onClose} bendsToDiscard={bendsToDiscard} />}
    </Box>
  );
};
